'use client';
import { FabricViewMode, Level } from '@susu/custom-made/utils/constants';
import type {
  LayerDefinitions,
  OptionsSelection,
  StylePresets,
  typeOptionValue,
} from '@susu/custom-made/utils/interfaces';
import { getConfigurationTypeBySection, getPreviewSlideIndex } from '@susu/custom-made/utils/navigation';
import { getExtraProductPreviewLayers, getProductPreviewLayers } from '@susu/custom-made/utils/preview/layers';
import { ConfigurationTypes, type ProductType } from '@susu/custom-made/utils/productDefinition';
import { Products } from '@susu/custom-made/utils/productDefinition';
import { PREVIEW_HEADER_TEST_IDS } from '@susu/custom-made/utils/testIds';
import {
  getConfigurationTypeKey,
  isAdditonalProduct,
  isExtraConfigurationInProduct,
} from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import type { RefObject } from 'react';
import { Fragment, lazy, memo, Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import Button, { ButtonSize, ButtonType } from '../button/button';
import type { PreviewProps } from '../preview/preview';
import Preview from '../preview/preview';
import PreviewSlider from '../previewSlider/previewSlider';
import Toast, { ToastType } from '../toast/toast';
import Loader from '../loader/loader';
const OVERLAY_BASE =
  'absolute top-0 bottom-0 w-[100dvw] bg-gray-300/75 z-[1] flex justify-center items-center lg:w-[60dvw]';

const getOverlayLeftClass = (index: number) => {
  switch (index) {
    case 1:
      return 'left-[100dvw] lg:left-[60dvw]';
    case 2:
      return 'left-[calc(100dvw*2)] lg:left-[calc(60dvw*2)]';
    case 3:
      return 'left-[calc(100dvw*3)] lg:left-[calc(60dvw*3)]';
    default:
      return 'left-0';
  }
};
import { useAdditionalProduct } from '@susu/custom-made/hooks/useAdditionalProduct';
import { useMissingValues } from '@susu/custom-made/hooks/useMissingValues';
import { useTotalPrice } from '@susu/custom-made/hooks/useTotalPrice';
import { useIsFirstRender } from '../sectionContainer/useFirstRender';
import { useEnvironmentContext } from '@susu/custom-made/providers/hooks/useEnvironmentContext';
import useFabricContext from '@susu/custom-made/providers/hooks/useFabricContext';
import useNavigationContext from '@susu/custom-made/providers/hooks/useNavigationContext';
import useGlobalContext from '@susu/custom-made/providers/hooks/useGlobalContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';
import { getModelViewDisplay } from '@susu/custom-made/hooks/useFabricViewMode';
import { useModelViewImage } from '@susu/custom-made/hooks/useModelViewImage';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';
import { useI18N } from '@susu/i18n/client';
import { formatPrice } from '@susu/custom-made/utils/formatPrice';

const { ENABLE_MODEL_VIEW } = FEATURE_TOGGLES;

const PreviewModeToggle = lazy(() => import('../previewModeToggle/previewModeToggle'));
const PreviewModel = lazy(() => import('../previewModel/previewModel'));

function PreviewHeader({
  layerDefinitions,
  optionValues,
  stylePresets,
  product,
  firstRenderRef,
  defaultLayers,
  isExtraProduct,
  fabricViewMode,
  onViewModeChange,
  onModelImageLoaded,
  onModelImageError,
  onFinishClick,
}: Readonly<{
  layerDefinitions: LayerDefinitions;
  optionValues: typeOptionValue;
  stylePresets: StylePresets;
  product: ProductType;
  firstRenderRef: RefObject<boolean>;
  defaultLayers: Map<string, PreviewProps>;
  isExtraProduct: boolean;
  fabricViewMode: FabricViewMode;
  onViewModeChange: (viewMode: FabricViewMode) => void;
  onModelImageLoaded: () => void;
  onModelImageError: () => void;
  onFinishClick: () => void;
}>) {
  const { t } = useI18N();
  const { section, level, isFinishButtonDisabled } = useNavigationContext();
  const modelViewFeatureEnabled = useFeatureIsOn(ENABLE_MODEL_VIEW);
  const [imageTemplates, setImageTemplates] = useState<Map<string, PreviewProps> | undefined>(undefined);
  const [sliderTogoIndex, setSliderTogoIndex] = useState<number | null>(null);
  const [isBaseStyleSelectionDone, setIsBaseStyleSelectionDone] = useState<boolean>(false);
  const [allOptionsAreSelected, setAllOptionsAreSelected] = useState<boolean>(false);
  const [missingSelectionFlow, setMissingSelectionFlow] = useState<boolean>(false);
  const { addAdditionalProductHandler } = useAdditionalProduct();
  const { cdnDomain } = useEnvironmentContext();
  const { selectedFabric } = useFabricContext();
  const { deliveryPeriod } = useGlobalContext();
  const totalPrice = useTotalPrice(optionValues);
  const isFirstRender = useIsFirstRender(firstRenderRef);
  const { areAllConfigurationMissingValuesEmpty, openMissingOptionValuesPanel } = useMissingValues();
  const hasMissingSelection = !areAllConfigurationMissingValuesEmpty();
  const { selectedOptions, hiddenOptionsAndValues, isAdditionalProductAdded, selectedBaseStyle } = useOptionContext();
  const {
    isImageError,
    modelViewImageData,
    modelViewThumbnailImageData,
    isModelViewImageLoading,
    isModelViewThumbnailImageLoading,
    modelViewImageLoadedHandler,
    modelViewThumbnailImageLoadedHandler,
    imageErrorHandler,
  } = useModelViewImage({ product, onLoad: onModelImageLoaded, onError: onModelImageError });
  const productLayers = useMemo(() => imageTemplates ?? defaultLayers, [imageTemplates]);
  const layeredProduct = productLayers.get(Products[product][0]);

  const modelViewThumbnailImage = {
    ...modelViewThumbnailImageData,
    isLoading: isModelViewThumbnailImageLoading,
    imageLoadedHandler: modelViewThumbnailImageLoadedHandler,
  };

  const setSliderSelection = useCallback(() => {
    const slideIndex = getPreviewSlideIndex(product, section, isExtraProduct);
    if (slideIndex !== null) {
      setSliderTogoIndex(slideIndex);
    }
  }, [product, section, isExtraProduct]);

  useEffect(() => {
    if (layerDefinitions && optionValues && selectedFabric && selectedOptions && section && !isFirstRender) {
      const selectedOptionsTmp: OptionsSelection = structuredClone(selectedOptions);

      try {
        setImageTemplates(
          isExtraProduct
            ? getExtraProductPreviewLayers(
                layerDefinitions,
                optionValues,
                selectedFabric,
                selectedOptionsTmp,
                hiddenOptionsAndValues,
                getConfigurationTypeBySection(section),
                cdnDomain,
              )
            : getProductPreviewLayers(
                layerDefinitions,
                optionValues,
                selectedFabric,
                selectedOptionsTmp,
                hiddenOptionsAndValues,
                product,
                cdnDomain,
              ),
        );
      } catch (error) {
        console.error('Custom made - Preview layers Error', error);
      }
    }

    setSliderSelection();
  }, [
    layerDefinitions,
    optionValues,
    selectedFabric,
    selectedOptions,
    section,
    isAdditionalProductAdded,
    setSliderSelection,
    hiddenOptionsAndValues.optionsPerConfiguration,
    hiddenOptionsAndValues.optionValuesPerConfiguration,
    isFirstRender,
  ]);

  useEffect(() => {
    let baseStyleSelectionDone = true;
    Products[product].forEach(configurationTypeId => {
      if (
        stylePresets.configurationTypes[configurationTypeId] &&
        !isExtraConfigurationInProduct(product, configurationTypeId) &&
        !(
          selectedBaseStyle[configurationTypeId] ||
          (isAdditonalProduct(product, configurationTypeId) && !isAdditionalProductAdded[configurationTypeId])
        )
      ) {
        baseStyleSelectionDone = false;
      }
    });

    setIsBaseStyleSelectionDone(baseStyleSelectionDone);
  }, [selectedBaseStyle, product, isAdditionalProductAdded, stylePresets]);

  useEffect(() => {
    if (hasMissingSelection) {
      setMissingSelectionFlow(true);
    }
    if (!hasMissingSelection && missingSelectionFlow) {
      setAllOptionsAreSelected(true);
      setTimeout(() => {
        setAllOptionsAreSelected(false);
      }, 3000);
    }
  }, [hasMissingSelection, missingSelectionFlow]);

  const {
    isModelViewActive,
    isToggleShown,
    isToggleHidden,
  } = getModelViewDisplay({
    isModelViewEnabled: modelViewFeatureEnabled,
    fabricViewMode,
    section,
    isImageError,
    isImageLoadingOnFirstRender: Boolean(isFirstRender) && (isModelViewThumbnailImageLoading || isModelViewImageLoading),
  });

  const isPreviewModeToggleWithPadding = product === 'Suit';

  return (
    <div className="relative h-full w-full overflow-hidden">
      {modelViewFeatureEnabled && (
        <Suspense fallback={<Loader isContentSizeLoader />}>
          <PreviewModel
            hidden={!isModelViewActive}
            modelViewImageData={modelViewImageData}
            isModelViewImageLoading={isModelViewImageLoading}
            modelViewImageLoadedHandler={modelViewImageLoadedHandler}
            modelViewImageErrorHandler={imageErrorHandler}
          />
        </Suspense>
      )}
      <PreviewSlider sliderTogoIndex={sliderTogoIndex} hidden={isModelViewActive}>
        {Array.from(productLayers).map(([key, value], index) => {
          return (
            <Fragment key={index}>
              {!isExtraProduct &&
                isAdditonalProduct(product!, key as ConfigurationTypes) &&
                !isAdditionalProductAdded[key] && (
                  <div className={cn(OVERLAY_BASE, getOverlayLeftClass(index))}>
                  <div
                    className="first-letter:uppercase rounded-[24px] py-4 px-16 font-light text-sm leading-sm text-gray-600 border border-solid [border-image-source:linear-gradient(90deg,rgba(255,255,255,0.5)_0%,rgba(255,255,255,0)_51.84%,rgba(255,255,255,0.5)_100%)] bg-white/50 backdrop-blur-lg cursor-pointer hover:bg-white/75"
                    onClick={() => addAdditionalProductHandler(key as ConfigurationTypes)}
                    data-testid={PREVIEW_HEADER_TEST_IDS.ADD_ADDITIONAL_PRODUCT_BTN}
                  >
                    {t('previewHeader.noProductAdded', { configurationType: t(`configurationType.${getConfigurationTypeKey(key as ConfigurationTypes)}`).toLowerCase() })}
                  </div>
                  </div>
                )}
              <Preview
                key={key}
                configurationType={key as ConfigurationTypes}
                presetIndex={index}
                layers={value.layers}
                fallbackImage={value.fallbackImage}
                isZoomable={true}
                isPreviewLoaderActive={!isFirstRender}
              />
            </Fragment>
          );
        })}
      </PreviewSlider>
      <div className="pt-16 pr-12 pl-16 absolute top-0 left-0 md:p-20 lg:py-20 lg:pr-20 lg:pl-24">
        <div className="text-base font-medium leading-base" data-testid={PREVIEW_HEADER_TEST_IDS.PRICE}>
          {t('previewHeader.total')} {formatPrice(totalPrice, selectedFabric?.prices?.code ?? '')}
        </div>
        {level === Level.Group && deliveryPeriod && (
          <div className="text-sm font-light leading-sm" data-testid={PREVIEW_HEADER_TEST_IDS.DELIVERY}>
            {t('previewHeader.deliveryWeeks', { min: String(deliveryPeriod.minDurationInWeeks), max: String(deliveryPeriod.maxDurationInWeeks) })}
          </div>
        )}
      </div>
      {level === Level.Group && (
        <div className="pt-[14px] pr-16 pl-14 absolute top-0 right-0 md:p-20 lg:hidden">
          <Button
            onClick={onFinishClick}
            testId={PREVIEW_HEADER_TEST_IDS.SAVE_BTN}
            data-dd-action-name="Finish"
            isDisabled={isFinishButtonDisabled}
            type={isBaseStyleSelectionDone ? ButtonType.Primary : ButtonType.Secondary}
            size={ButtonSize.Large}
            noBorder={true}
          >
            {t('previewHeader.finish')}
          </Button>
        </div>
      )}
      {isToggleShown && (
        <div className={cn('absolute bottom-16 right-16 lg:bottom-20 lg:right-20', { hidden: isToggleHidden })}>
          <Suspense fallback={null}>
            {layeredProduct && (
              <PreviewModeToggle
                viewMode={fabricViewMode}
                modelViewImage={modelViewThumbnailImage}
                isLayersLoading={isModelViewActive && isModelViewImageLoading}
                layersViewComponent={
                  <Preview
                    configurationType={Products[product][0]}
                    layers={layeredProduct.layers}
                    fallbackImage={layeredProduct.fallbackImage}
                    isPreviewLoaderActive={isModelViewActive && isModelViewImageLoading}
                  />
                }
                withPadding={isPreviewModeToggleWithPadding}
                onViewModeChange={onViewModeChange}
                imageErrorHandler={imageErrorHandler}
              />
            )}
          </Suspense>
        </div>
      )}
      <div>
        {hasMissingSelection && (
          <Toast
            type={ToastType.Warning}
            btnText={t('previewHeader.missingOptionsToast.btn')}
            text={t('previewHeader.missingOptionsToast.text')}
            hasBtn={true}
            icon={'alert'}
            isHidden={false}
            btnClickHandler={openMissingOptionValuesPanel}
          />
        )}
        <Toast
          type={ToastType.Regular}
          btnText={t('previewHeader.allOptionsUpdatedToast.btn')}
          text={t('previewHeader.allOptionsUpdatedToast.text')}
          hasBtn={false}
          icon={'checkmark'}
          isHidden={!allOptionsAreSelected}
          btnClickHandler={openMissingOptionValuesPanel}
        />
      </div>
    </div>
  );
}

export default memo(PreviewHeader);
